import React, { FC, useEffect, useRef, useState } from "react";

import { FormDropdownInput } from "stentor-models";

import { FormFieldProps } from "../FormDisplay";
import { ArrowDropDownIcon } from "../icons";

import styles from "./FormFieldDropdown.module.scss";

const PLACEHOLDER_LABEL = "Select an option";

/**
 * Extended SelectableItem interface to support free-form text input for dropdown items.
 * This allows dropdown options to trigger an additional text input field when selected.
 *
 * Note: This extends the stentor-models SelectableItem type with additional properties.
 * Consider adding these properties to stentor-models SelectableItem in a future release
 * if this pattern becomes widely used across the platform.
 */
export interface SelectableItemWithFreeForm {
    label: string;
    id: string;
    selected?: boolean;
    /**
     * If true, shows a free-form text input field when this item is selected.
     * Useful for "Other" options that require additional user input.
     */
    freeFormInput?: boolean;
    /**
     * Custom field name for the free-form input value.
     * Defaults to "message" if not specified.
     * This allows multiple dropdowns to have different free-form field names.
     */
    freeFormFieldName?: string;
}

export interface FormDropdownProps extends FormFieldProps {
    readonly field: FormDropdownInput;
}

export const FormFieldDropdown: FC<FormDropdownProps> = (props) => {
    const { field, onFieldChange, errorMessage, required, freeFormErrorMessage } = props;

    const helperId = `${field.name}-helper-text`;
    const freeFormId = `${field.name}-free-form`;
    const freeFormHelperId = `${freeFormId}-helper-text`;

    const [dropValue, setDropValue] = useState(() => {
        const selectedItem = field.items.find(item => item.selected);
        return selectedItem?.id || "none";
    });
    const [freeFormValue, setFreeFormValue] = useState("");
    const [showFreeForm, setShowFreeForm] = useState(() => {
        const selectedItem = field.items.find(item => item.selected) as SelectableItemWithFreeForm;
        return selectedItem?.freeFormInput || false;
    });

    const onChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { value } = e.target;

        // Get the previous selected item to clear its free-form value if needed
        const prevSelectedItem = field.items.find(
            (item) => item.id === dropValue
        ) as SelectableItemWithFreeForm;

        setDropValue(value);

        // Check if the selected item has freeFormInput enabled
        const selectedItem = field.items.find(
            (item) => item.id === value
        ) as SelectableItemWithFreeForm;
        if (selectedItem && selectedItem.freeFormInput) {
            setShowFreeForm(true);
        } else {
            setShowFreeForm(false);

            // Only clear if switching away from a free-form item AND there was actually a value
            if (prevSelectedItem?.freeFormInput && freeFormValue) {
                const messageFieldName = prevSelectedItem?.freeFormFieldName || "message";
                setFreeFormValue(""); // Clear local state
                onFieldChange(messageFieldName, ""); // Clear parent state
            }
        }
    };

    const onFreeFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFreeFormValue(e.target.value);
    };

    // Track onFieldChange reference to detect if it's not memoized in development
    const onFieldChangeRef = useRef(onFieldChange);
    useEffect(() => {
        if (process.env.NODE_ENV === "development" && onFieldChangeRef.current !== onFieldChange) {
            console.warn(
                "FormFieldDropdown: onFieldChange prop is not memoized. " +
                "This may cause performance issues. Please wrap it in useCallback in the parent component."
            );
        }
        onFieldChangeRef.current = onFieldChange;
    }, [onFieldChange]);

    // Get the current free-form field name if applicable
    const currentFreeFormFieldName = React.useMemo(() => {
        if (showFreeForm) {
            const selectedItem = field.items.find(
                (item) => item.id === dropValue
            ) as SelectableItemWithFreeForm;
            return selectedItem?.freeFormFieldName || "message";
        }
        return null;
    }, [showFreeForm, dropValue, field.items]);

    // IMPORTANT: Parent components MUST use useCallback for onFieldChange
    // to prevent infinite re-renders. See FormStepDisplay.tsx:352-373 for correct usage.
    useEffect(() => {
        // Send the raw dropdown ID value (formatting happens during text interpolation)
        if (dropValue !== "none") {
            onFieldChange(field.name, dropValue);
        }

        // If freeform is active and has a value, also send it to the configured field name
        if (showFreeForm && freeFormValue && currentFreeFormFieldName) {
            onFieldChange(currentFreeFormFieldName, freeFormValue);
        }
    }, [dropValue, freeFormValue, showFreeForm, field.name, currentFreeFormFieldName, onFieldChange]);

    // A native <select> at every width. Mobile already had it -- the OS wheel/sheet picker,
    // momentum scrolling, no focus trap inside the fixed modal -- and desktop now gets the same
    // control in place of MUI's listbox (#1741). The outline and floating label are drawn to
    // match the outlined MUI field it replaces.
    return (
        <div className={styles.xapp_dropdown_container}>
            <div className={`${styles.xapp_dropdown_control} ${errorMessage ? styles.xapp_dropdown_error : ""}`}>
                <label htmlFor={field.name} className={styles.xapp_dropdown_label}>
                    {field.title}
                    {required && <span aria-hidden="true">{" *"}</span>}
                </label>
                <select
                    className={styles.xapp_dropdown_select}
                    name={field.name}
                    id={field.name}
                    value={dropValue}
                    onChange={onChange}
                    required={required}
                    aria-invalid={!!errorMessage}
                    aria-describedby={errorMessage ? helperId : undefined}>
                    <option value="none">{PLACEHOLDER_LABEL}</option>
                    {field.items.map((item) => (
                        <option key={item.id} value={item.id}>
                            {item.label}
                        </option>
                    ))}
                </select>
                <ArrowDropDownIcon className={styles.xapp_dropdown_arrow} />
                <fieldset aria-hidden="true" className={styles.xapp_dropdown_outline}>
                    <legend>
                        <span>
                            {field.title}
                            {required && " *"}
                        </span>
                    </legend>
                </fieldset>
            </div>
            {errorMessage && (
                <p id={helperId} className={styles.xapp_dropdown_helper}>
                    {errorMessage}
                </p>
            )}

            {showFreeForm && (
                <div className={styles.xapp_dropdown_free_form}>
                    <input
                        type="text"
                        id={freeFormId}
                        className={`${styles.xapp_dropdown_free_form_input} ${
                            freeFormErrorMessage ? styles.xapp_dropdown_error : ""
                        }`}
                        required
                        placeholder="Please specify"
                        aria-label="Please specify"
                        value={freeFormValue}
                        onChange={onFreeFormChange}
                        maxLength={field.maxLength || undefined}
                        aria-invalid={!!freeFormErrorMessage}
                        aria-describedby={freeFormErrorMessage ? freeFormHelperId : undefined}
                    />
                    {freeFormErrorMessage && (
                        <p id={freeFormHelperId} className={styles.xapp_dropdown_helper}>
                            {freeFormErrorMessage}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default FormFieldDropdown;
